import { useQuery } from '@tanstack/react-query';
import { careerFitService } from '@/services/careerFitService';
import type {
  CareerFitResult,
  CareerFitSubmitResult,
} from '@/types/academy/careerFit';

export const CAREER_FIT_QUERY_KEY = ['academy', 'career-fit'] as const;

export const careerFitLatestResultQueryKey = [
  ...CAREER_FIT_QUERY_KEY,
  'latest',
] as const;

/**
 * Fetches the Career Fit Check questions and track metadata.
 * Content is static server-side, so it is cached for the session.
 *
 * Pass `enabled: false` to hold the request back until the questions are
 * actually needed — the intro screen renders without them.
 */
export const useCareerFit = (enabled = true) =>
  useQuery<CareerFitResult | null>({
    queryKey: CAREER_FIT_QUERY_KEY,
    queryFn: async () => {
      const response = await careerFitService.getCareerFit();
      return response.result ?? null;
    },
    enabled,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

/**
 * Fetches the user's most recent assessment result, or null when they have never
 * taken one. Lets returning users land on their result instead of the intro.
 *
 * Pass `enabled: false` for guests — the endpoint is authenticated.
 */
export const useCareerFitLatestResult = (enabled = true) =>
  useQuery<CareerFitSubmitResult | null>({
    queryKey: careerFitLatestResultQueryKey,
    queryFn: async () => {
      const response = await careerFitService.getLatestResult();
      return response.result ?? null;
    },
    enabled,
    refetchOnWindowFocus: false,
  });
